import { setUser } from "@sentry/react"
import api from "api"
import { AuthData } from "api/resources/auth/types"
import { identifyHubSpotUser, resetHubSpotUser } from "hubspot"
import snakeCase from "lodash/snakeCase"
import posthog from "posthog-js"
import { BROWSER_STORAGE_KEYS } from "utils/constants"
import { buildUserName } from "utils/helpers"
import { isShadow } from "utils/shadow"

import { GA } from "./ga"
import { getUTMParamsForMercury } from "./helper"
import {
  clearSentPersonProperties,
  queuePersonProperties,
} from "./personProperties"
import { initStatwideTracking } from "./statwide"

type Service = "GA" | "PostHog" | "HubSpot"

const allServices: Service[] = ["GA", "PostHog"]

type Config = {
  services?: Service[]
}

const getCookie = (name: string) => {
  try {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop()?.split(";").shift() ?? null
    return null
  } catch (e) {
    console.error(e)
    return null
  }
}

export const trackingService = {
  initUser: (
    authInfo: {
      user: Pick<AuthData["user"], "firstName" | "lastName" | "uuid" | "email">
    } & Pick<AuthData, "shadow">
  ) => {
    console.log("> Initializing tracking user:", authInfo)

    if (authInfo.shadow || isShadow()) return

    GA.setUserId(authInfo.user.uuid)

    posthog?.identify(authInfo.user.uuid, {
      email: authInfo.user.email,
      name: buildUserName(authInfo.user),
      first_name: authInfo.user.firstName,
      last_name: authInfo.user.lastName,
    })

    // Sentry
    setUser({ id: authInfo.user.uuid, email: authInfo.user.email })

    initStatwideTracking(authInfo.user)

    try {
      identifyHubSpotUser({
        email: authInfo.user.email,
        firstName: authInfo.user.firstName,
        lastName: authInfo.user.lastName,
      })
    } catch (e) {
      console.warn("Error identifying user on HubSpot", e)
    }
  },
  trackEvent: (eventName: string, data?: object, config: Config = {}) => {
    // Adding UTM params into all events (picked up from cache if not present in the URL)
    const utmParams = trackingService.getUTMParams()
    data = {
      ...utmParams,
      ...(data || {}),
    }

    // If services are not specified then send to all services
    const services = config.services || allServices

    console.log(
      `Tracking event: ${eventName} on ${services.join(", ")}`,
      data,
      config
    )

    // console.log(`Tracking ${eventName} on services `, services, data)

    if (isShadow()) return
    try {
      if (services.includes("PostHog")) {
        posthog.capture(eventName, data)
      }
    } catch (e) {
      console.warn(`Error tracking event on PostHog: ${eventName}`, data, e)
    }

    try {
      if (services.includes("GA")) {
        // @ts-expect-error we can't type this
        GA.trackEvent(eventName, data)
      }
    } catch (e) {
      console.warn(`Error tracking event on GA: ${eventName}`, data, e)
    }

    try {
      if (services.includes("HubSpot")) {
        if (typeof window !== "undefined" && window._hsq) {
          const utmKeys = [
            "utm_source",
            "utm_medium",
            "utm_campaign",
            "utm_term",
            "utm_content",
          ]

          // Prepend hs_ to UTM keys
          utmKeys.forEach(key => {
            if (data && key in data) {
              data[`hs_${key}`] = data[key]
              delete data[key]
            }
          })

          const additionalParams = trackingService.getAdditionalParams()
          data = {
            ...additionalParams,
            ...(data || {}),
          }

          window._hsq.push([
            "trackCustomBehavioralEvent",
            {
              name: eventName, // MUST be HubSpot-defined (e.g. pe48613855_scroll_depth)
              properties: data || {},
            },
          ])
        }
      }
    } catch (e) {
      console.warn(`Error tracking event on HubSpot: ${eventName}`, data, e)
    }
  },
  setUserAttribute: (key: string, value: any, services = allServices) => {
    if (isShadow()) return

    // console.log(`> Tracking user attribute: ${key}=${value}`)
    try {
      if (services.includes("PostHog")) {
        queuePersonProperties({ [key]: value })
      }
    } catch (e) {
      console.warn(`Error tracking user attribute on PostHog: ${key}`, value)
      console.warn(e)
    }
  },

  setPostHogUserAttribute: (properties: Record<string, any>) => {
    if (isShadow()) return

    queuePersonProperties(properties)
  },

  getSignupSource: (source?: string) => {
    const searchParams = new URLSearchParams(window.location.search)

    const utmParams = trackingService.getUTMParams()

    const value =
      source ||
      searchParams.get("signup_source") ||
      utmParams.utm_source ||
      document.referrer ||
      "direct"

    return value
  },

  reset: () => {
    resetHubSpotUser()
    clearSentPersonProperties()
    posthog?.reset()
  },

  getUTMParams: (useCache = true): UTMParams => {
    const getUTMParamValueFromStorage = (key: string) => {
      const storedParams = sessionStorage.getItem(
        BROWSER_STORAGE_KEYS.sessionUtmParams
      )
      if (!storedParams) return null

      try {
        const params = JSON.parse(storedParams)
        return params[key] || null
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        return null
      }
    }

    const params = new URLSearchParams(window.location.search)

    const keys = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
    ]

    const utmParams = {}

    keys.forEach(key => {
      const value =
        params.get(key) || (useCache ? getUTMParamValueFromStorage(key) : null)
      if (value) utmParams[key] = value
    })

    return utmParams
  },
  getAdditionalParams: (): AdditionalParams => {
    const searchParams = new URLSearchParams(window.location.search)

    const ipAddress = localStorage.getItem(BROWSER_STORAGE_KEYS.ipAddress)

    return {
      browser_user_agent: navigator.userAgent,
      ip_address: ipAddress,
      marketing___audience: searchParams.get("audience"),
      marketing___google_click_id: searchParams.get("gclid"),
      marketing___landing_page_variant: searchParams.get("var"),
      marketing___meta_browser_id: getCookie("_fbp"),
      marketing___meta_click_id: searchParams.get("fbclid"),
      marketing___session_referrer_url: document.referrer || null,
      marketing___source_page_url: searchParams.get("utm_source_url"),
    }
  },
}

type UTMParams = {
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
}

type AdditionalParams = {
  browser_user_agent: string
  ip_address: string | null
  marketing___audience: string | null
  marketing___google_click_id: string | null
  marketing___landing_page_variant: string | null
  marketing___meta_browser_id: string | null
  marketing___meta_click_id: string | null
  marketing___session_referrer_url: string | null
  marketing___source_page_url: string | null
}

/**
 *
 * @param params Prefer writing object keys in snake case. If you don't then they'll automatically be converted to snake case.
 */
export const trackElementOnPH = (params: Record<string, any>) => {
  const formattedParams: Record<string, any> = {
    "data-ph-autocapture": true,
  }
  Object.entries(params).forEach(([key, value]) => {
    formattedParams[`data-ph-capture-attribute-${snakeCase(key)}`] = value
  })

  return formattedParams
}

type LeadProps = {
  fullName: string
  email: string
  phoneNumber?:
    | string
    | {
        code: string | number
        number: string | number
      }
    | null
  data?: Record<string, any>
  originDetails: {
    brand: "suraasa"
    product: "pgctl" | "cpat" | "suraasa_plus"
    activityMode: "online"
    activityChannel: "website"
    activityType: "product_purchase"
    activityDetail: "direct_payment_method" | "teacher_career_resolution_2026"
  }
  utm_parameters?: Record<string, any>
}

export const createLead = async (props: LeadProps) => {
  if (typeof window === "undefined") return
  if (isShadow()) return

  const searchParams: Record<string, any> = {}

  new URLSearchParams(window.location.search).forEach((value, key) => {
    searchParams[key] = value
  })

  props.utm_parameters =
    props.utm_parameters ||
    getUTMParamsForMercury(searchParams, document.referrer)
  try {
    await api.users.createLead({ data: props })
  } catch (error) {
    console.error("Error creating lead:", error)
  }
}
